import { useContext, Fragment } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { Box, Flex, Text } from 'rebass'
import { MediaQueries as Mq } from '../../styles/settings/theme'
import { makeDate } from '../../utils/helpers'
import PersonFetcher from '../organisms/PersonFetcher'
import ConnectionGroup from '../molecules/ConnectionGroup'
import { DataContext } from '../../contexts/dataContext'
import { lang } from '../../utils/lang'
import { getFaNum } from '../../utils/helpers'
import SmImg from '../../assets/thumb_small.png'
import { Theme } from '../../styles/settings/theme'

const Container = styled(Box)`
  padding: 1rem 1rem 2.25rem 1rem;
  ${Mq.xs} {
    padding: 1rem 1.75rem 2.25rem 1.75rem;
  }
`

const HeaderText = styled(Text)`
  font-family: ${(props) => props.theme.fonts.condensed};
  font-size: 1.5rem;
  font-weight: 400;
  padding: 3rem 0 0.5rem 0;
  line-height: 1.17;
  span {
    font-size: 2rem;
    font-weight: 700;
  }
  ${Mq.xs} {
    padding: 0 0 0.5rem 0;
  }
`
const ClickText = styled(Text)`
  font-size: 0.8rem;
  padding: 0.5rem 0 0 0;
  ${Mq.xs} {
    font-size: 1rem;
  }
`
const Line = styled(Box)`
  display: none;
  ${Mq.xs} {
    display: block;
    border-top: 1px solid ${(props) => props.theme.colors.white};
    margin: ${(props) =>
      props.ui === 'en' ? '0.2rem 0 0 0.5rem' : '0.2rem .5rem 0 0'};
    flex: 1;
  }
`
const ViolationLegend = styled(Flex)`
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  margin-top: 0;
  ${Mq.xs} {
    margin-top: -0.5rem;
  }
`
const Dot = styled(Box)`
  width: 0.8rem;
  height: 0.8rem;
  border-radius: 50%;
`
const LegendLabel = styled(Box)`
  font-family: ${(props) => props.theme.fonts.condensed};
  font-size: 0.9rem;
  font-weight: 700;
`
const Grid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 0.6rem;
  margin-top: 0.75rem;
  ${Mq.xs} {
    grid-template-columns: 1fr 1fr;
  }
`
const Record = styled(Flex)`
  border: 2px solid transparent;
  &:hover {
    border: 2px solid ${(props) => props.theme.colors.aqua};
    border-radius: 0.25rem;
  }
  cursor: pointer;
`
const PicBox = styled(Box)`
  border: ${(props) => (props.noImg ? `1px solid white` : null)};
  border-radius: ${(props) =>
    props.ui === 'en' ? '0.25rem 0 0 0.25rem' : '0 0.25rem   0.25rem 0'};
  width: 4rem;
  height: 100%;
  img {
    border-radius: ${(props) =>
      props.ui === 'en' ? '0.25rem 0 0 0.25rem' : '0 0.25rem   0.25rem 0'};
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`
const InfoBox = styled(Flex)`
  flex-direction: column;
  width: 100%;
  padding: 0.5rem;
  background-color: ${(props) => props.theme.colors.midnightBlue};
  padding-left: 0.75rem;
  border-radius: ${(props) =>
    props.ui === 'en' ? '0 0.25rem 0.25rem 0' : '0.25rem 0 0 0.25rem'};
  border-top: 1px solid ${(props) => props.theme.colors.black};
  border-right: ${(props) =>
    props.ui === 'en' ? `1px solid ${props.theme.colors.black}` : 'none'};
  border-left: ${(props) =>
    props.ui === 'en' ? 'none' : `1px solid ${props.theme.colors.black}`};
  border-bottom: 1px solid ${(props) => props.theme.colors.black};
`
const Name = styled(Box)`
  font-family: ${(props) => props.theme.fonts.condensed};
  font-size: 1.1rem;
  font-weight: 700;
`
const InfoLine = styled(Flex)`
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;
`
const DateLine = styled(Flex)`
  align-items: center;
  flex-wrap: wrap;
`
const AgeLine = styled(Flex)`
  align-items: center;
  padding: ${(props) => (props.ui === 'en' ? ' 0 2rem 0 0' : '0 0 0 2rem')};
`
const Label = styled(Text)`
  font-weight: 400;
  font-size: ${(props) => (props.ui === 'en' ? '0.75rem' : '.9rem')};
  padding-top: ${(props) => (props.ui === 'en' ? '1px' : 0)};
  text-transform: uppercase;
`
const Data = styled(Text)`
  font-weight: 400;
  font-size: 0.9rem;
  white-space: nowrap;
`
const MobileBox = styled(Box)`
  ${Mq.xs} {
    display: none;
  }
`

const ListOfRecords = ({
  records,
  handleId,
  personId,
  handleNonFatalCase,
  nonFatalCase
}) => {
  const { ui } = useContext(DataContext)
  const dir = ui === 'en' ? 'ltr' : 'rtl'
  const numViolations = Object.entries(records).length

  const sortedRecords = records.reduce((obj, record) => {
    if (obj[record.properties.id]) {
      obj[record.properties.id].push(record)
    } else obj[record.properties.id] = [record]
    return obj
  }, {})

  const numVictims = Object.keys(sortedRecords).length
  if (personId) {
    return (
      <div>
        <PersonFetcher
          personId={personId}
          handleId={handleId}
          nonFatalCase={nonFatalCase}
          //needed to gather all dates
          allRecords={sortedRecords[personId]}
        />
      </div>
    )
  }
  //if need go back button, put button in PersonFetcher,
  //make it call handler in here (ListOfRecords) that sets personId back to null.

  return (
    <Container dir={dir}>
      <MobileBox>
        <ConnectionGroup
          flushLeft={ui === 'en' ? true : false}
          inModal={true}
        />
      </MobileBox>
      <HeaderText dir={dir}>
        <span>{ui === 'en' ? numViolations : getFaNum(numViolations)}</span>{' '}
        {numViolations > 1 ? lang[ui]['Violations'] : lang[ui]['Violation']}
        {lang[ui]['Comma']}{' '}
        <span>{ui === 'en' ? numVictims : getFaNum(numVictims)}</span>{' '}
        {numVictims > 1 ? lang[ui]['Victims'] : lang[ui]['Victim']}&nbsp;
        {lang[ui]['InLocation']}
      </HeaderText>
      <Flex alignItems="center">
        <ClickText>{lang[ui]['click on name']}</ClickText>
        <Line ui={ui} />
      </Flex>
      <ViolationLegend>
        <Dot
          bg={Theme.colors.red}
          my={2}
          mr={ui === 'en' ? 2 : 0}
          ml={ui === 'en' ? 0 : 2}
        />
        <LegendLabel mr={ui === 'en' ? 3 : 0} ml={ui === 'en' ? 0 : 3}>
          {lang[ui]['EJK']}
        </LegendLabel>

        <Dot
          bg={Theme.colors.orange}
          my={2}
          mr={ui === 'en' ? 2 : 0}
          ml={ui === 'en' ? 0 : 2}
        />
        <LegendLabel>{lang[ui]['Attempts/Threats']}</LegendLabel>
      </ViolationLegend>
      <Grid>
        {Object.values(sortedRecords)
          .sort((a, b) =>
            a[0].properties.date > b[0].properties.date ? 1 : -1
          )
          .map((recordArray, i) => {
            let threatDates = []
            //if (recordArray.length > 1) {
            var sortedRecordArrayByDate = recordArray.sort((a, b) =>
              +a.properties.date > +b.properties.date ? 1 : -1
            )
            sortedRecordArrayByDate.forEach((record) =>
              record.properties.nonFatal
                ? threatDates.push(record.properties.date)
                : null
            )
            // }
            const { fnames, sname, date, age, id, photoUrlSm, nonFatal } =
              sortedRecordArrayByDate[recordArray.length - 1].properties //need to look at last one in array to make sure we catch nonFatal accurately. (When sorted by date, killing will always be last.)
            //console.log('sorted', sortedRecordArrayByDate)
            return (
              <Record
                key={`${id}${i}`}
                onClick={() => {
                  handleNonFatalCase(nonFatal)
                  handleId(id)
                }}
              >
                <PicBox noImg={photoUrlSm ? false : true} ui={ui}>
                  <img src={photoUrlSm || SmImg} alt={fnames + ' ' + sname} />
                </PicBox>
                <InfoBox ui={ui}>
                  <Flex>
                    <Name>
                      {fnames}&nbsp;{sname}
                    </Name>
                    {/* {sortedRecordArrayByDate.map((record, i) => (
                    <Dot
                      bg={
                        record.properties.nonFatal
                          ? Theme.colors.orange
                          : Theme.colors.red
                      }
                      my={2}
                      mr={ui === 'en' ? 0 : 1}
                      ml={ui === 'en' ? 1 : 0}
                    />
                  ))} */}
                  </Flex>

                  {recordArray.length > 1 ? (
                    sortedRecordArrayByDate.map((record, i) => {
                      return (
                        <InfoLine>
                          {record.properties.nonFatal && !i ? (
                            <DateLine>
                              <Dot
                                bg={Theme.colors.orange}
                                //my={2}
                                mr={ui === 'en' ? 2 : 0}
                                ml={ui === 'en' ? 0 : 2}
                              />
                              <Label ui={ui}>
                                {lang[ui]['DateThreat']}: &nbsp;
                              </Label>
                              {threatDates.map((date, i) => (
                                <Fragment key={date}>
                                  <Data>
                                    {!date || date === '0'
                                      ? lang[ui]['Unknown']
                                      : makeDate(date, ui)}
                                  </Data>
                                  {i < threatDates.length - 1 ? (
                                    <Data> , &nbsp; </Data>
                                  ) : null}
                                </Fragment>
                              ))}
                            </DateLine>
                          ) : (
                            <>
                              {!record.properties.nonFatal ? (
                                <>
                                  <DateLine>
                                    <Dot
                                      bg={Theme.colors.red}
                                      //my={2}
                                      mr={ui === 'en' ? 2 : 0}
                                      ml={ui === 'en' ? 0 : 2}
                                    />
                                    <Label ui={ui}>
                                      {lang[ui]['DateKilling']}: &nbsp;
                                    </Label>
                                    <Data>
                                      {!date || date === '0'
                                        ? lang[ui]['Unknown']
                                        : makeDate(date, ui)}
                                    </Data>
                                  </DateLine>
                                  {age !== '0' && (
                                    <AgeLine ui={ui}>
                                      <Label ui={ui}>
                                        {lang[ui]['Age']}: &nbsp;
                                      </Label>
                                      <Data fontWeight="400" fontSize=".9rem">
                                        {!age
                                          ? lang[ui]['Unknown']
                                          : ui === 'en'
                                          ? age
                                          : getFaNum(age)}
                                      </Data>
                                    </AgeLine>
                                  )}
                                </>
                              ) : null}
                            </>
                          )}
                        </InfoLine>
                      )
                    })
                  ) : (
                    <InfoLine>
                      <DateLine>
                        <Dot
                          bg={nonFatal ? Theme.colors.orange : Theme.colors.red}
                          //my={2}
                          mr={ui === 'en' ? 2 : 0}
                          ml={ui === 'en' ? 0 : 2}
                        />
                        <Label ui={ui}>
                          {lang[ui][nonFatal ? 'DateThreat' : 'DateKilling']}:
                          &nbsp;
                        </Label>

                        <Data>
                          {!date || date === '0'
                            ? lang[ui]['Unknown']
                            : makeDate(date, ui)}
                        </Data>
                      </DateLine>

                      {age !== '0' && !nonFatal && (
                        <AgeLine ui={ui}>
                          <Label ui={ui}>{lang[ui]['Age']}: &nbsp;</Label>
                          <Data fontWeight="400" fontSize=".9rem">
                            {!age
                              ? lang[ui]['Unknown']
                              : ui === 'en'
                              ? age
                              : getFaNum(age)}
                          </Data>
                        </AgeLine>
                      )}
                    </InfoLine>
                  )}
                </InfoBox>
              </Record>
            )
          })}
      </Grid>
    </Container>
  )
}

export default ListOfRecords

ListOfRecords.propTypes = {
  records: PropTypes.array.isRequired,
  handleId: PropTypes.func.isRequired,
  personId: PropTypes.number
}
